import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "search",
    "count",
    "chips",
    "list",
    "option",
    "checkbox",
    "clear",
    "noResults",
    "roleSelect",
    "roleField",
    "locationSelect",
    "locationField",
    "assignmentFields",
  ];

  static values = {
    multiLocation: Boolean,
    locations: Array,
  };

  connect() {
    this.handleRoomsChanged = this.handleRoomsChanged.bind(this);
    document.addEventListener("room-booking:rooms-changed", this.handleRoomsChanged);

    this.checkboxTargets.forEach((checkbox) => this.syncAssignmentFields(checkbox));
    this.render();
  }

  disconnect() {
    document.removeEventListener("room-booking:rooms-changed", this.handleRoomsChanged);
  }

  handleRoomsChanged(event) {
    const locations = event.detail?.locations || [];
    this.locationsValue = locations;
    this.multiLocationValue = locations.length > 1;
    this.refreshLocationSelects();
    this.render();
  }

  filter() {
    const query = this.hasSearchTarget
      ? this.searchTarget.value.trim().toLowerCase()
      : "";

    let visibleCount = 0;

    this.optionTargets.forEach((option) => {
      const haystack = option.dataset.staffSearch || "";
      const matches = query === "" || haystack.includes(query);
      option.classList.toggle("d-none", !matches);
      if (matches) visibleCount += 1;
    });

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("d-none", visibleCount > 0);
    }
  }

  toggle(event) {
    const option = event.target.closest(".rb-staff-option");
    if (option) {
      option.classList.toggle("is-selected", event.target.checked);
    }
    this.syncAssignmentFields(event.target);
    this.render();
  }

  clearAll() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
      const option = checkbox.closest(".rb-staff-option");
      if (option) option.classList.remove("is-selected");
      this.syncAssignmentFields(checkbox);
    });
    this.render();
  }

  removeChip(event) {
    const { staffId } = event.currentTarget.dataset;
    const checkbox = this.checkboxTargets.find((box) => box.value === staffId);
    if (!checkbox) return;

    checkbox.checked = false;
    const option = checkbox.closest(".rb-staff-option");
    if (option) option.classList.remove("is-selected");
    this.syncAssignmentFields(checkbox);
    this.render();
  }

  roleChanged() {
    this.render();
  }

  locationChanged() {
    this.render();
  }

  stopRoleInteraction(event) {
    event.stopPropagation();
  }

  render() {
    const selected = this.checkboxTargets.filter((checkbox) => checkbox.checked);

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${selected.length} selected`;
    }

    if (this.hasClearTarget) {
      this.clearTarget.classList.toggle("d-none", selected.length === 0);
    }

    if (!this.hasChipsTarget) return;

    this.chipsTarget.innerHTML = "";

    selected.forEach((checkbox) => {
      const chip = document.createElement("span");
      chip.className = "rb-chip";

      const label = document.createElement("span");
      label.textContent = this.chipLabel(checkbox);
      chip.appendChild(label);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "rb-chip__remove";
      remove.innerHTML = "&times;";
      remove.setAttribute(
        "aria-label",
        `Remove ${checkbox.dataset.staffName || "staff member"}`
      );
      remove.dataset.staffId = checkbox.value;
      remove.dataset.action = "room-booking-staff-picker#removeChip";
      chip.appendChild(remove);

      this.chipsTarget.appendChild(chip);
    });
  }

  syncAssignmentFields(checkbox) {
    const option = checkbox.closest(".rb-staff-option");
    if (!option) return;

    const roleSelect = option.querySelector(
      '[data-room-booking-staff-picker-target="roleSelect"]'
    );
    if (roleSelect) {
      roleSelect.disabled = !checkbox.checked;
      roleSelect.classList.toggle("d-none", !checkbox.checked);
    }

    const locationSelect = option.querySelector(
      '[data-room-booking-staff-picker-target="locationSelect"]'
    );
    const locationField = option.querySelector(
      '[data-room-booking-staff-picker-target="locationField"]'
    );
    if (locationSelect) {
      locationSelect.disabled = !checkbox.checked;
      const showLocation = checkbox.checked && this.multiLocationValue;
      locationSelect.classList.toggle("d-none", !showLocation);
      if (locationField) locationField.classList.toggle("d-none", !this.multiLocationValue);
      if (showLocation && !locationSelect.value && this.locationsValue.length === 1) {
        locationSelect.value = String(this.locationsValue[0].id);
      }
    }
  }

  refreshLocationSelects() {
    this.locationFieldTargets.forEach((field) => {
      field.classList.toggle("d-none", !this.multiLocationValue);
    });

    this.locationSelectTargets.forEach((select) => {
      const currentValue = select.value;
      select.innerHTML = "";

      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Select location";
      select.appendChild(placeholder);

      this.locationsValue.forEach((location) => {
        const option = document.createElement("option");
        option.value = String(location.id);
        option.textContent = location.name;
        select.appendChild(option);
      });

      if (currentValue && this.locationsValue.some((location) => String(location.id) === currentValue)) {
        select.value = currentValue;
      } else if (this.locationsValue.length === 1) {
        select.value = String(this.locationsValue[0].id);
      } else {
        select.value = "";
      }

      const checkbox = select.closest(".rb-staff-option")?.querySelector(
        '[data-room-booking-staff-picker-target="checkbox"]'
      );
      if (checkbox) this.syncAssignmentFields(checkbox);
    });
  }

  chipLabel(checkbox) {
    const name = checkbox.dataset.staffName || "Staff member";
    const option = checkbox.closest(".rb-staff-option");
    if (!option) return name;

    const parts = [name];

    const roleSelect = option.querySelector(
      '[data-room-booking-staff-picker-target="roleSelect"]'
    );
    if (roleSelect && roleSelect.value) {
      const roleName = roleSelect.options[roleSelect.selectedIndex]?.textContent?.trim();
      if (roleName) parts.push(roleName);
    }

    const locationSelect = option.querySelector(
      '[data-room-booking-staff-picker-target="locationSelect"]'
    );
    if (this.multiLocationValue && locationSelect && locationSelect.value) {
      const locationName =
        locationSelect.options[locationSelect.selectedIndex]?.textContent?.trim();
      if (locationName && locationName !== "Select location") parts.push(locationName);
    }

    return parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(" · ")})` : name;
  }
}
